import { getFlagUrl } from "./nationalityFlags";

export type BadgePattern =
  | "solid"
  | "v_halves"
  | "h_halves"
  | "v_stripes"
  | "h_stripes"
  | "sash"
  | "v_thirds"
  | "h_thirds"
  | "quarters"
  | "thin_v_stripes";

export interface BadgeConfig {
  primary: string;
  secondary: string;
  tertiary?: string;
  pattern: BadgePattern;
  /** When set, render a flag image clipped to the shield instead of the colour pattern */
  flagUrl?: string;
}

/** Maps club names (as stored in the DB) to a simplified badge config. */
const clubBadges: Record<string, BadgeConfig> = {
  // ── Premier League ──────────────────────────────────────────────────────────
  Arsenal: { primary: "#EF0107", secondary: "#FFFFFF", pattern: "v_halves" },
  "Aston Villa": { primary: "#95BFE5", secondary: "#670E36", pattern: "v_halves" },
  Bournemouth: { primary: "#DA291C", secondary: "#000000", tertiary: "#FFFFFF", pattern: "v_stripes" },
  Brentford: { primary: "#E30613", secondary: "#FFFFFF", tertiary: "#000000", pattern: "v_stripes" },
  "Brighton & Hove Albion": { primary: "#0057B8", secondary: "#FFFFFF", pattern: "v_stripes" },
  Brighton: { primary: "#0057B8", secondary: "#FFFFFF", pattern: "v_stripes" },
  Chelsea: { primary: "#034694", secondary: "#FFFFFF", pattern: "solid" },
  "Crystal Palace": { primary: "#1B458F", secondary: "#C4122E", pattern: "v_halves" },
  Everton: { primary: "#003399", secondary: "#FFFFFF", pattern: "solid" },
  Fulham: { primary: "#FFFFFF", secondary: "#000000", pattern: "v_halves" },
  "Ipswich Town": { primary: "#3A64A3", secondary: "#FFFFFF", pattern: "solid" },
  Ipswich: { primary: "#3A64A3", secondary: "#FFFFFF", pattern: "solid" },
  "Leicester City": { primary: "#003090", secondary: "#FDBE11", pattern: "solid" },
  Leicester: { primary: "#003090", secondary: "#FDBE11", pattern: "solid" },
  Liverpool: { primary: "#C8102E", secondary: "#F6EB61", pattern: "solid" },
  "Manchester City": { primary: "#6CABDD", secondary: "#FFFFFF", pattern: "solid" },
  "Manchester United": { primary: "#DA291C", secondary: "#FBE122", pattern: "solid" },
  "Newcastle United": { primary: "#241F20", secondary: "#FFFFFF", pattern: "v_stripes" },
  Newcastle: { primary: "#241F20", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Nottingham Forest": { primary: "#DD0000", secondary: "#FFFFFF", pattern: "solid" },
  "Nottm Forest": { primary: "#DD0000", secondary: "#FFFFFF", pattern: "solid" },
  Southampton: { primary: "#D71920", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Tottenham Hotspur": { primary: "#FFFFFF", secondary: "#132257", pattern: "solid" },
  Spurs: { primary: "#FFFFFF", secondary: "#132257", pattern: "solid" },
  "West Ham United": { primary: "#7A263A", secondary: "#1BB1E7", pattern: "v_halves" },
  "West Ham": { primary: "#7A263A", secondary: "#1BB1E7", pattern: "v_halves" },
  "Wolverhampton Wanderers": { primary: "#FDB913", secondary: "#231F20", pattern: "solid" },
  Wolves: { primary: "#FDB913", secondary: "#231F20", pattern: "solid" },

  // ── Championship ────────────────────────────────────────────────────────────
  "Blackburn Rovers": { primary: "#009EE0", secondary: "#FFFFFF", pattern: "h_halves" },
  "Bristol City": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  Burnley: { primary: "#6C1D45", secondary: "#99D6EA", pattern: "v_halves" },
  "Cardiff City": { primary: "#0070B5", secondary: "#FFFFFF", pattern: "solid" },
  "Coventry City": { primary: "#87CEFA", secondary: "#003DA5", pattern: "solid" },
  "Derby County": { primary: "#FFFFFF", secondary: "#000000", pattern: "solid" },
  "Hull City": { primary: "#F18A00", secondary: "#000000", pattern: "v_stripes" },
  "Leeds United": { primary: "#FFCD00", secondary: "#1D428A", pattern: "solid" },
  Leeds: { primary: "#FFCD00", secondary: "#1D428A", pattern: "solid" },
  "Luton Town": { primary: "#F78F1E", secondary: "#FFFFFF", pattern: "solid" },
  Middlesbrough: { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  Millwall: { primary: "#004B87", secondary: "#FFFFFF", pattern: "solid" },
  "Norwich City": { primary: "#00A650", secondary: "#FFF200", pattern: "v_halves" },
  Norwich: { primary: "#00A650", secondary: "#FFF200", pattern: "v_halves" },
  "Oxford United": { primary: "#FFCD00", secondary: "#14284B", pattern: "solid" },
  "Plymouth Argyle": { primary: "#006B35", secondary: "#FFFFFF", pattern: "solid" },
  Portsmouth: { primary: "#001489", secondary: "#FFFFFF", pattern: "solid" },
  "Preston North End": { primary: "#FFFFFF", secondary: "#1E3765", pattern: "v_stripes" },
  "Queens Park Rangers": { primary: "#1D5BA4", secondary: "#FFFFFF", pattern: "h_stripes" },
  QPR: { primary: "#1D5BA4", secondary: "#FFFFFF", pattern: "h_stripes" },
  "Sheffield United": { primary: "#EE2737", secondary: "#FFFFFF", tertiary: "#000000", pattern: "v_stripes" },
  "Sheffield Wednesday": { primary: "#0066B2", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Stoke City": { primary: "#E03A3E", secondary: "#FFFFFF", pattern: "v_stripes" },
  Sunderland: { primary: "#EB172B", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Swansea City": { primary: "#FFFFFF", secondary: "#121212", pattern: "solid" },
  Watford: { primary: "#FBEE23", secondary: "#ED2127", tertiary: "#000000", pattern: "v_thirds" },
  "West Bromwich Albion": { primary: "#122F67", secondary: "#FFFFFF", pattern: "v_stripes" },
  "West Brom": { primary: "#122F67", secondary: "#FFFFFF", pattern: "v_stripes" },

  // ── League One ──────────────────────────────────────────────────────────────
  Barnsley: { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Birmingham City": { primary: "#0000CD", secondary: "#FFFFFF", pattern: "solid" },
  Blackpool: { primary: "#F4793B", secondary: "#FFFFFF", pattern: "solid" },
  "Bolton Wanderers": { primary: "#FFFFFF", secondary: "#263C7E", pattern: "solid" },
  "Bristol Rovers": { primary: "#003DA5", secondary: "#FFFFFF", pattern: "quarters" },
  "Burton Albion": { primary: "#F0B41C", secondary: "#000000", pattern: "solid" },
  "Cambridge United": { primary: "#F4A830", secondary: "#000000", pattern: "solid" },
  "Charlton Athletic": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Crawley Town": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Exeter City": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Huddersfield Town": { primary: "#0E63AD", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Leyton Orient": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Lincoln City": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Mansfield Town": { primary: "#ECAD00", secondary: "#003DA5", pattern: "solid" },
  "Northampton Town": { primary: "#831F2D", secondary: "#FFFFFF", pattern: "solid" },
  "Peterborough United": { primary: "#0096D6", secondary: "#FFFFFF", pattern: "solid" },
  Reading: { primary: "#003DA5", secondary: "#FFFFFF", pattern: "solid" },
  "Rotherham United": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Shrewsbury Town": { primary: "#003DA5", secondary: "#F5A623", pattern: "v_halves" },
  Stevenage: { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Stockport County": { primary: "#003DA5", secondary: "#FFFFFF", pattern: "solid" },
  "Wigan Athletic": { primary: "#003DA5", secondary: "#FFFFFF", pattern: "solid" },
  Wrexham: { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Wycombe Wanderers": { primary: "#41B6E6", secondary: "#1C3360", pattern: "quarters" },

  // ── League Two ──────────────────────────────────────────────────────────────
  "AFC Wimbledon": { primary: "#003DA5", secondary: "#FFCD00", pattern: "solid" },
  "Accrington Stanley": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  Barrow: { primary: "#003DA5", secondary: "#FFFFFF", pattern: "solid" },
  "Bradford City": { primary: "#832080", secondary: "#F9A11B", pattern: "v_halves" },
  Bromley: { primary: "#000000", secondary: "#FFCD00", pattern: "solid" },
  "Carlisle United": { primary: "#003DA5", secondary: "#DA291C", pattern: "v_halves" },
  "Cheltenham Town": { primary: "#DA291C", secondary: "#FFCD00", pattern: "solid" },
  Chesterfield: { primary: "#003DA5", secondary: "#FFFFFF", pattern: "solid" },
  "Colchester United": { primary: "#003DA5", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Crewe Alexandra": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Doncaster Rovers": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Fleetwood Town": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  Gillingham: { primary: "#003DA5", secondary: "#000000", pattern: "solid" },
  "Grimsby Town": { primary: "#000000", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Harrogate Town": { primary: "#FFCD00", secondary: "#000000", pattern: "solid" },
  "MK Dons": { primary: "#FFCD00", secondary: "#000000", pattern: "solid" },
  Morecambe: { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Newport County": { primary: "#F4A830", secondary: "#000000", pattern: "solid" },
  "Notts County": { primary: "#000000", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Port Vale": { primary: "#FFFFFF", secondary: "#000000", pattern: "solid" },
  "Salford City": { primary: "#DA291C", secondary: "#000000", pattern: "solid" },
  "Swindon Town": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Tranmere Rovers": { primary: "#FFFFFF", secondary: "#003DA5", pattern: "solid" },
  Walsall: { primary: "#DA291C", secondary: "#000000", pattern: "solid" },

  // ── Spain ────────────────────────────────────────────────────────────────────
  "Real Madrid": { primary: "#FFFFFF", secondary: "#00529F", pattern: "solid" },
  "Barcelona": { primary: "#A50044", secondary: "#004D98", pattern: "v_stripes" },
  "Atletico Madrid": { primary: "#CC0000", secondary: "#FFFFFF", pattern: "h_stripes" },
  "Sevilla": { primary: "#FFFFFF", secondary: "#D40000", pattern: "v_halves" },
  "Real Sociedad": { primary: "#0070B5", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Athletic Bilbao": { primary: "#EE2737", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Villarreal": { primary: "#FFCD00", secondary: "#005580", pattern: "solid" },
  "Valencia": { primary: "#FF6600", secondary: "#000000", pattern: "v_halves" },
  "Betis": { primary: "#00954C", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Real Betis": { primary: "#00954C", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Girona": { primary: "#CC0000", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Mallorca": { primary: "#DA291C", secondary: "#000000", pattern: "v_stripes" },
  "Osasuna": { primary: "#DA291C", secondary: "#003DA5", pattern: "v_stripes" },

  // ── Germany ──────────────────────────────────────────────────────────────────
  "Bayern Munich": { primary: "#DC052D", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Bayern München": { primary: "#DC052D", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Borussia Dortmund": { primary: "#FDE100", secondary: "#000000", pattern: "solid" },
  "Bayer Leverkusen": { primary: "#E32221", secondary: "#000000", pattern: "v_halves" },
  "RB Leipzig": { primary: "#CC0000", secondary: "#FFFFFF", pattern: "solid" },
  "Eintracht Frankfurt": { primary: "#000000", secondary: "#E1000F", pattern: "v_halves" },
  "Borussia Mönchengladbach": { primary: "#00933A", secondary: "#FFFFFF", pattern: "v_stripes" },
  "VfB Stuttgart": { primary: "#FFFFFF", secondary: "#E32219", pattern: "v_stripes" },
  Stuttgart: { primary: "#FFFFFF", secondary: "#E32219", pattern: "v_stripes" },
  "Werder Bremen": { primary: "#1D9053", secondary: "#FFFFFF", pattern: "v_halves" },
  "Wolfsburg": { primary: "#65B32E", secondary: "#FFFFFF", pattern: "solid" },
  "Hoffenheim": { primary: "#1961AC", secondary: "#FFFFFF", pattern: "solid" },
  "Union Berlin": { primary: "#EB1923", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Freiburg": { primary: "#E3051A", secondary: "#000000", tertiary: "#FFFFFF", pattern: "v_stripes" },
  "Augsburg": { primary: "#BA3733", secondary: "#007A47", tertiary: "#FFFFFF", pattern: "v_thirds" },
  "Mainz": { primary: "#CC0000", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Heidenheim": { primary: "#CC0000", secondary: "#1A1A1A", pattern: "v_halves" },

  // ── Italy ────────────────────────────────────────────────────────────────────
  "AC Milan": { primary: "#FB090B", secondary: "#000000", pattern: "v_stripes" },
  "Inter Milan": { primary: "#0068A8", secondary: "#000000", pattern: "v_stripes" },
  "Juventus": { primary: "#000000", secondary: "#FFFFFF", pattern: "v_halves" },
  "Napoli": { primary: "#12A0C3", secondary: "#FFFFFF", pattern: "solid" },
  "Roma": { primary: "#9B1A22", secondary: "#F5A623", pattern: "solid" },
  "AS Roma": { primary: "#9B1A22", secondary: "#F5A623", pattern: "solid" },
  "Lazio": { primary: "#87CEEB", secondary: "#FFFFFF", pattern: "solid" },
  "Atalanta": { primary: "#1E62AE", secondary: "#000000", pattern: "v_stripes" },
  "Fiorentina": { primary: "#5E2D91", secondary: "#FFFFFF", pattern: "solid" },
  "Torino": { primary: "#8B1A1A", secondary: "#FFFFFF", pattern: "solid" },
  "Bologna": { primary: "#DA291C", secondary: "#003DA5", pattern: "h_halves" },
  "Udinese": { primary: "#000000", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Monza": { primary: "#FFFFFF", secondary: "#DA291C", pattern: "v_stripes" },
  "Cagliari": { primary: "#1E1E1E", secondary: "#DA291C", pattern: "v_halves" },
  "Lecce": { primary: "#FFCC00", secondary: "#8B1A1A", pattern: "v_stripes" },
  "Empoli": { primary: "#004E96", secondary: "#FFFFFF", pattern: "solid" },
  "Sassuolo": { primary: "#00663A", secondary: "#000000", pattern: "v_halves" },

  // ── France ───────────────────────────────────────────────────────────────────
  "Paris Saint-Germain": { primary: "#002F5F", secondary: "#DA291C", tertiary: "#FFFFFF", pattern: "h_thirds" },
  PSG: { primary: "#002F5F", secondary: "#DA291C", pattern: "solid" },
  "Olympique de Marseille": { primary: "#2CB0E8", secondary: "#FFFFFF", pattern: "solid" },
  Marseille: { primary: "#2CB0E8", secondary: "#FFFFFF", pattern: "solid" },
  "Monaco": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "h_halves" },
  "AS Monaco": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "h_halves" },
  "Olympique Lyonnais": { primary: "#FFFFFF", secondary: "#DA291C", tertiary: "#003DA5", pattern: "v_thirds" },
  Lyon: { primary: "#FFFFFF", secondary: "#DA291C", pattern: "v_halves" },
  "Lille": { primary: "#EE1C25", secondary: "#003DA5", pattern: "v_halves" },
  "Lens": { primary: "#D0A132", secondary: "#DA291C", pattern: "solid" },
  "Nice": { primary: "#000000", secondary: "#DA291C", pattern: "v_stripes" },
  "Rennes": { primary: "#000000", secondary: "#DA291C", pattern: "h_halves" },

  // ── Netherlands ──────────────────────────────────────────────────────────────
  "Ajax": { primary: "#CC0000", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Feyenoord": { primary: "#DA291C", secondary: "#000000", tertiary: "#FFFFFF", pattern: "v_thirds" },
  "PSV Eindhoven": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "v_stripes" },
  PSV: { primary: "#DA291C", secondary: "#FFFFFF", pattern: "v_stripes" },
  "AZ Alkmaar": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },

  // ── Portugal ─────────────────────────────────────────────────────────────────
  "Benfica": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Porto": { primary: "#003DA5", secondary: "#FFFFFF", tertiary: "#0059A6", pattern: "v_stripes" },
  "Sporting CP": { primary: "#006600", secondary: "#FFFFFF", pattern: "solid" },
  "Braga": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },

  // ── International / other ────────────────────────────────────────────────────
  "Fenerbahçe": { primary: "#FFCC00", secondary: "#003DA5", pattern: "v_halves" },
  "Galatasaray": { primary: "#DA291C", secondary: "#FFCD00", pattern: "h_halves" },
  "Besiktas": { primary: "#000000", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Celtic": { primary: "#1BAE53", secondary: "#FFFFFF", pattern: "v_stripes" },
  "Rangers": { primary: "#003DA5", secondary: "#FFFFFF", pattern: "solid" },
  "Club Brugge": { primary: "#003DA5", secondary: "#000000", pattern: "v_halves" },
  "Anderlecht": { primary: "#7B2D8B", secondary: "#FFFFFF", pattern: "solid" },
  "Red Bull Salzburg": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Shakhtar Donetsk": { primary: "#F47920", secondary: "#000000", pattern: "v_stripes" },
  "Dinamo Zagreb": { primary: "#003DA5", secondary: "#FFFFFF", pattern: "solid" },
  "Benficá": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Olympiacos": { primary: "#DA291C", secondary: "#FFFFFF", pattern: "solid" },
  "Panathinaikos": { primary: "#006600", secondary: "#FFFFFF", pattern: "solid" },
  "PAOK": { primary: "#000000", secondary: "#FFFFFF", pattern: "h_halves" },
  "Crvena zvezda": { primary: "#CC0000", secondary: "#FFFFFF", pattern: "solid" },
  "Red Star Belgrade": { primary: "#CC0000", secondary: "#FFFFFF", pattern: "solid" },
  "Partizan": { primary: "#000000", secondary: "#FFFFFF", pattern: "v_halves" },
  "Zenit": { primary: "#003DA5", secondary: "#FFFFFF", pattern: "solid" },
  "CSKA Moscow": { primary: "#003DA5", secondary: "#DA291C", pattern: "v_halves" },
};

/** Default badge for unknown clubs */
const defaultBadge: BadgeConfig = {
  primary: "#666666",
  secondary: "#CCCCCC",
  pattern: "solid",
};

export function getClubBadge(clubName: string): BadgeConfig {
  if (!clubName) return defaultBadge;
  // Exact match
  if (clubBadges[clubName]) return clubBadges[clubName];
  // Partial match (club name contains key or key contains club name)
  const lower = clubName.toLowerCase();
  for (const [key, config] of Object.entries(clubBadges)) {
    if (lower.includes(key.toLowerCase()) || key.toLowerCase().includes(lower)) {
      return config;
    }
  }
  // Fall back to country flag for national team names
  const flagUrl = getFlagUrl(clubName);
  if (flagUrl) {
    return { primary: "#888888", secondary: "#CCCCCC", pattern: "solid", flagUrl };
  }
  return defaultBadge;
}
